/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Frontend SPA Contract API
 * Canonical API contract entrypoint.
 *
 * Source architecture:
 * - split by module / bounded context
 * - one file per aggregate root
 * - child entities remain owned by aggregate root
 * - cross-cutting technical concerns live under `openapi/common`
 *
 * OpenAPI spec version: 1.0.0
 */
import type {
  CustomerOfferBindingDefinitionDto,
  CustomerOfferDto,
  ListCustomerOffersParams,
  NoteId,
  NoteInput,
  PaginatedCustomerOfferDto,
  PrepareCustomerOfferSendResult,
  SendCustomerOfferRequest,
  SetCustomerOfferChapterCommercialOptionsRequest,
  SetCustomerOfferCoefficientRequest,
  UpdateDraftCustomerOfferRequest,
} from "../model";

import {customFetch} from "../../../runtime/custom-fetch";

export const getListCustomerOffersUrl = (params?: ListCustomerOffersParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    const explodeParameters = ["statuses"];

    if (Array.isArray(value) && explodeParameters.includes(key)) {
      value.forEach((v) => {
        normalizedParams.append(key, v === null ? "null" : String(v));
      });
      return;
    }

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/v1/sales/customer-offers?${stringifiedParams}`
    : `/api/v1/sales/customer-offers`;
};

/**
 * Full DTOs with permitted evaluation. Independent filters combine by AND; statuses matches any selected status, intersecting status when both are supplied. Omitted status and statuses impose no status restriction. Literal case-insensitive q searches the canonical displayed name; surrounding whitespace is trimmed and blank q imposes no restriction. Wildcards and regular-expression syntax have no special meaning. latestOnly selects the highest committed version visible in the projection for each expedientId and offerOrdinal before all visible filters, including version, and before counting or pagination. A nonmatching or OBSOLETE head never causes fallback to an older revision. Default listing retains every revision. Projection visibility is eventual, not write authorization or proof of successor absence. Sort defaults name ascending with UUID ascending tie-break; version descending browses one chain's history. Trusted LIVE commercial calculation may use protected inputs; available selling remains visible while private fields and coefficient values follow their independent disclosure rules.
 * @summary List Offer revisions
 */
export const listCustomerOffers = async (
  params?: ListCustomerOffersParams,
  options?: Parameters<typeof customFetch>[1],
): Promise<PaginatedCustomerOfferDto> => {
  return customFetch<PaginatedCustomerOfferDto>(getListCustomerOffersUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}`;
};

/**
 * Every unissued phase, including COEFFICIENT_REVIEW, readiness and unissued OBSOLETE, uses LIVE facts. Issued outcomes and issued OBSOLETE use the sole frozen document with current per-field disclosure. Known costs do not require coefficients; unpriced selling is NOT_PRICED, not zero or restricted. Ordinary read plus protected-price observe controls direct coefficient values, independently of lawful unprotected finance. Older documents use older IDs. Inputs appear once; no raw snapshot. A stored state inconsistent with issued-document presence fails technically; it never substitutes a live or issued representation.
 * @summary Get Offer revision and permitted evaluated document
 */
export const getCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<CustomerOfferDto> => {
  return customFetch<CustomerOfferDto>(getGetCustomerOfferUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getUpdateDraftCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}`;
};

/**
 * Latest DRAFT and ACTIVE dossier only; complete language/currency/content replacement. Currency must have defined JDK minor-unit precision for monetary quotation, even with rich-text-only content. Unsupported precision returns the existing support.domain.money.undefined-precision validation Problem with HTTP 400, without committing the replacement. Shared Currency recognition is unchanged; do not substitute Currency or precision. Check authoritative no-successor ownership in the same decision boundary before assembly or financial acquisition. Retain creator, captured parties, identity and Notes. Introduced/changed supplier selections must be empty and unresolved, or nonempty with complete exact distinct SIMPLE-leaf coverage. Reject nonempty partial coverage; unchanged later source drift remains located issues. Other unresolved DRAFT slots remain allowed. Protected financial changes require permission; opaque text/layout repair grants no repricing. New catalog blocks or changed sold identities/kinds require an actual description in the input language, including with authored null quantity or empty supplier choices; otherwise return sales.customerOffer.invalidSelection with SOURCE_MISSING and unchanged revision. Language changes retain existing inputs but missing localized descriptions withhold affected prices and block positive progression until repaired; no old-language fallback or issued-history rewrite.
 * @summary Replace draft Offer inputs
 */
export const updateDraftCustomerOffer = async (
  id: string,
  updateDraftCustomerOfferRequest: UpdateDraftCustomerOfferRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getUpdateDraftCustomerOfferUrl(id), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(updateDraftCustomerOfferRequest),
  });
};

export const getSubmitCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/submit`;
};

/**
 * Latest DRAFT to PENDING content review with ACTIVE parent. Require complete current authored inputs, exact leaf coverage, eligible same-Currency PRICED sources and resolved variables/descriptions and selected Commission. Coefficients and selling calculations are not required or defaulted. Null quantity calculates as one; positive fractions remain valid, zero/negative do not default. MOQ/OQM stay informational. Preserve existing applicable protected-action authority.
 * @summary Submit draft for review
 */
export const submitCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getSubmitCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getApproveCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/approve`;
};

/**
 * Latest PENDING to COEFFICIENT_REVIEW with ACTIVE parent and valid current authored inputs, not completed selling pricing. Content approver is authenticated actor. This does not enter readiness or authorize Send. Existing applicable protected-action authority remains; valid source-only price drift does not add another approval stage.
 * @summary Approve content into coefficient review
 */
export const approveCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getApproveCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getMoveCustomerOfferToDraftUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/return-to-draft`;
};

/**
 * Latest PENDING/COEFFICIENT_REVIEW/APPROVED to DRAFT under existing write authority, ACTIVE parent and absent issued document. Clear all coefficient decisions and current review/readiness attribution; retain authored content, flags, captured parties and Notes. No source-validity requirement. Issued facts never become editable.
 * @summary Move never-issued Offer to draft for repair
 */
export const moveCustomerOfferToDraft = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getMoveCustomerOfferToDraftUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getSendCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/send`;
};

/**
 * Latest APPROVED to SENT under unchanged Send/conditional protected management authority, ACTIVE parent and no prior issuance. Validate fresh complete facts against confirmed commercial content/selling terms and server mutation/stage identity. Changed confirmation returns sales.customerOffer.confirmationStale with HTTP 409 and no issued Event. Hidden internal drift with unchanged confirmed commercial terms uses fresh internal facts. Freeze the same evaluation that was compared, never a recapture or viewer DTO. After snapshot validation, check the captured ECB publication again at the final issuance decision without recapture. A crossed deadline returns sales.customerOffer.exchangeRatesUnavailable with HTTP 409 and no Event, even if a newer publication is now installed. A qualifying publication change absorbed by selling rounding does not alone stale confirmation. Same-currency issuance needs no ECB publication. No automatic email delivery. Reconcile uncertain committed acknowledgement by reads, never blind replay.
 * @summary Record immutable issuance
 */
export const sendCustomerOffer = async (
  id: string,
  sendCustomerOfferRequest: SendCustomerOfferRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getSendCustomerOfferUrl(id), {
    ...options,
    method: "POST",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(sendCustomerOfferRequest),
  });
};

export const getAcceptCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/accept`;
};

/**
 * Latest SENT to ACCEPTED; ACTIVE dossier. Check actual no-successor ownership in the same decision boundary. Use exact issued terms without current Engineering/user reevaluation. Project readiness remains outside scope.
 * @summary Record acceptance of issued terms
 */
export const acceptCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getAcceptCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getRejectCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/reject`;
};

/**
 * Latest SENT to REJECTED; ACTIVE dossier. Check actual no-successor ownership in the same decision boundary, without source validation or automatic successor. Previous issued document remains immutable.
 * @summary Record customer rejection
 */
export const rejectCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRejectCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getCancelCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/cancel`;
};

/**
 * Latest SENT to CANCELED; ACTIVE dossier. Check actual no-successor ownership in the same decision boundary, without source validation or canceling the owning Expedient/project.
 * @summary Record customer cancellation
 */
export const cancelCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getCancelCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getCreateCustomerOfferRevisionUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/revisions`;
};

/**
 * REJECTED source, no successor, ACTIVE dossier and authorization required. New DRAFT identity/version; retain chain/predecessor, copy structural content/language/currency/Notes and every authored input value and flag, regardless of remembering, including null quantities, literal/binding values and complete supplier selections. Preserve stale copied references for repairable current evaluation issues, without pruning or defaulting; protected financial copying still requires authorization. Template opening alone retains remembered clearing and empty new Notes. Capture current eligible parties and new creator. Start with unset content approval/readiness and no coefficient decisions. Recalculate live sources; never copy an issued calculation as current authority. A concurrent event-store append rejection remains a technical HTTP 500, not a deterministic stage-conflict HTTP 409. Reconcile the current revision and chain before another explicit action; never automatically replay an accepted write.
 * @summary Create explicit successor revision
 */
export const createCustomerOfferRevision = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<string> => {
  return customFetch<string>(getCreateCustomerOfferRevisionUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getAddCustomerOfferNoteUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/notes`;
};

/**
 * DRAFT and ACTIVE dossier only. Check current authoritative stage before Note identity allocation or input assembly. Historical REJECTED revisions remain read-only; creating a successor does not reopen their Notes.
 * @summary Add draft Offer Note
 */
export const addCustomerOfferNote = async (
  id: string,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<NoteId> => {
  return customFetch<NoteId>(getAddCustomerOfferNoteUrl(id), {
    ...options,
    method: "POST",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(noteInput),
  });
};

export const getEditCustomerOfferNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/customer-offers/${id}/notes/${noteId}`;
};

/**
 * DRAFT and ACTIVE dossier only; preserve Note identity. Check current authoritative stage before owned Note lookup or input assembly. Historical REJECTED revisions remain read-only.
 * @summary Replace draft Offer Note
 */
export const editCustomerOfferNote = async (
  id: string,
  noteId: NoteId,
  noteInput: NoteInput,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getEditCustomerOfferNoteUrl(id, noteId), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(noteInput),
  });
};

export const getRemoveCustomerOfferNoteUrl = (id: string, noteId: NoteId) => {
  return `/api/v1/sales/customer-offers/${id}/notes/${noteId}`;
};

/**
 * DRAFT and ACTIVE dossier only. Check current authoritative stage before owned Note lookup. Historical REJECTED revisions remain read-only.
 * @summary Remove draft Offer Note
 */
export const removeCustomerOfferNote = async (
  id: string,
  noteId: NoteId,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRemoveCustomerOfferNoteUrl(id, noteId), {
    ...options,
    method: "DELETE",
  });
};

export const getListCustomerOfferBindingDefinitionsUrl = () => {
  return `/api/v1/sales/customer-offers/binding-definitions`;
};

/**
 * Requires ordinary Resource read permission, not an existing Resource. Return all supported keys, including currently unresolvable keys, in stable order: $me.email then $technical_director. English labels and descriptions are metadata, not resolved values or availability claims. Creator email uses the Offer creator, never its viewer. No arbitrary subject, expression or user-directory lookup is accepted. Existing evaluation reports unresolved values; literals stay literal and issued text remains frozen independently of discovery.
 * @summary Discover supported editor binding definitions
 */
export const listCustomerOfferBindingDefinitions = async (
  options?: Parameters<typeof customFetch>[1],
): Promise<CustomerOfferBindingDefinitionDto[]> => {
  return customFetch<CustomerOfferBindingDefinitionDto[]>(
    getListCustomerOfferBindingDefinitionsUrl(),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getObsoleteCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/obsolete`;
};

/**
 * Requires existing Offer write, ACTIVE parent and authoritative absence of a successor, including retries. Record actual prior stage once; preserve identity/version, content, parties, Notes, review attribution, protected coefficient progress and any frozen document. Repeated OBSOLETE is a guarded no-op and never replaces its origin. Preserve actor/time/from/to audit without a mandatory reason. Unissued obsolete remains LIVE; issued obsolete remains fixed ISSUED. Reconcile committed acknowledgement by reads, not blind replay. A concurrent event-store append rejection remains a technical HTTP 500, not a deterministic stage-conflict HTTP 409. Reconcile the current revision and chain before another explicit action; never automatically replay an accepted write.
 * @summary Mark an Offer obsolete without changing its issued terms
 */
export const obsoleteCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getObsoleteCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getRecoverObsoleteCustomerOfferUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/recover`;
};

/**
 * Requires existing Offer write, ACTIVE parent, OBSOLETE and authoritative absence of a successor. Restore server-owned workflow.obsoleteFromStatus on the same identity/version, including private coefficient progress or the same frozen issued outcome. No caller destination, implicit Draft reset or repricing of issued terms. Source validity does not block undo; restored LIVE readiness exposes current repair qualification. Preserve actor/time/from/to audit. HTTP 204 means committed; reconcile current state without assuming DRAFT or replaying an accepted transition.
 * @summary Undo obsolescence to its recorded prior stage
 */
export const recoverObsoleteCustomerOffer = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getRecoverObsoleteCustomerOfferUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getSetCustomerOfferCoefficientUrl = (id: string, chapterId: string) => {
  return `/api/v1/sales/customer-offers/${id}/coefficients/${chapterId}`;
};

/**
 * Latest COEFFICIENT_REVIEW and ACTIVE parent only. Require ordinary Offer write plus existing protected-price observe, not an additional manage grant. Address an actual owned Chapter; coefficient must be positive with no maximum-one cap or clamping. No content edit or automatic completion. Saving may retain current source repair issues; read the current backend evaluation after committed acknowledgement.
 * @summary Set an admitted Chapter coefficient
 */
export const setCustomerOfferCoefficient = async (
  id: string,
  chapterId: string,
  setCustomerOfferCoefficientRequest: SetCustomerOfferCoefficientRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getSetCustomerOfferCoefficientUrl(id, chapterId), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(setCustomerOfferCoefficientRequest),
  });
};

export const getSetCustomerOfferChapterCommercialOptionsUrl = (id: string, chapterId: string) => {
  return `/api/v1/sales/customer-offers/${id}/chapters/${chapterId}/commercial-options`;
};

/**
 * Latest never-issued COEFFICIENT_REVIEW and ACTIVE parent only, with authoritative absence of a successor. Require the same ordinary Offer write, authenticated USER and protected-price observe authority as coefficient setting, including protected Chapters without extra manage. Set both explicit flags atomically for an actual owned Chapter. Preserve coefficients, content approval, identity/version, Notes and all other authored input. No coefficient, complete source pricing or exchange rates are required to save valid intent. Current repair issues remain; technical acquisition or authorization failures do not commit. Every admitted save advances the server mutation stamp, including identical values. No automatic completion, content replacement or replay deduplication. HTTP 204 is committed acknowledgement; reconcile through ordinary reads rather than replaying an accepted write after a failed read.
 * @summary Set Chapter commercial options during pricing review
 */
export const setCustomerOfferChapterCommercialOptions = async (
  id: string,
  chapterId: string,
  setCustomerOfferChapterCommercialOptionsRequest: SetCustomerOfferChapterCommercialOptionsRequest,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getSetCustomerOfferChapterCommercialOptionsUrl(id, chapterId), {
    ...options,
    method: "PUT",
    headers: (() => {
      const headers = new Headers(options?.headers);
      if (!headers.has("Content-Type")) headers.set("Content-Type", "application/json");
      return headers;
    })(),
    body: JSON.stringify(setCustomerOfferChapterCommercialOptionsRequest),
  });
};

export const getCompleteCustomerOfferCoefficientReviewUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/complete-coefficient-review`;
};

/**
 * Latest COEFFICIENT_REVIEW to APPROVED readiness with ACTIVE parent, ordinary Offer write and existing protected-price observe. Every Chapter, including optional and zero-cost Chapters, needs an admitted positive coefficient and complete current inputs/pricing. Explicit completion is not another human approval and sends nothing.
 * @summary Complete coefficient review and mark ready
 */
export const completeCustomerOfferCoefficientReview = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getCompleteCustomerOfferCoefficientReviewUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getReturnCustomerOfferToCoefficientReviewUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/return-to-coefficient-review`;
};

/**
 * Latest APPROVED to COEFFICIENT_REVIEW under ordinary Offer write and ACTIVE parent. Retain decisions/content/Notes, clear current readiness attribution and retain authored inputs. Only coefficients and the two Chapter commercial flags become editable under coefficient authority; other content and Notes remain read-only. Coefficient values remain protected by existing observe permission.
 * @summary Withdraw readiness for coefficient adjustment
 */
export const returnCustomerOfferToCoefficientReview = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getReturnCustomerOfferToCoefficientReviewUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getCorrectCustomerOfferOutcomeUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/correct-outcome`;
};

/**
 * Latest ACCEPTED/REJECTED/CANCELED to SENT with existing write, ACTIVE-parent and business guards. Race atomically with successor creation. Preserve identity/version and exact issued facts; change outcome only, with actor/time/from/to audit. Do not undo downstream commitments or cancel/restore the owning Expedient. A concurrent event-store append rejection remains a technical HTTP 500, not a deterministic stage-conflict HTTP 409. Reconcile the current revision and chain before another explicit action; never automatically replay an accepted write.
 * @summary Correct latest issued outcome to sent
 */
export const correctCustomerOfferOutcome = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<void> => {
  return customFetch<void>(getCorrectCustomerOfferOutcomeUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getPrepareCustomerOfferSendUrl = (id: string) => {
  return `/api/v1/sales/customer-offers/${id}/prepare-send`;
};

/**
 * Read-only business action under unchanged Send authorization. Require latest APPROVED, ACTIVE parent and complete current evaluation. Return commercial terms and an opaque actor/Offer/mutation-stage-bound token; no Event, early freeze, additional approval or hidden monetary digest. This token is confirmation evidence, never a permission grant.
 * @summary Prepare current commercial confirmation for issuance
 */
export const prepareCustomerOfferSend = async (
  id: string,
  options?: Parameters<typeof customFetch>[1],
): Promise<PrepareCustomerOfferSendResult> => {
  return customFetch<PrepareCustomerOfferSendResult>(getPrepareCustomerOfferSendUrl(id), {
    ...options,
    method: "POST",
  });
};
